// Horario de funcionamento da unidade: cada dia da semana fechado, ou aberto com um ou mais
// turnos ("11:00"-"15:00" e "18:00"-"23:00"). Turno que termina antes de comecar atravessa a
// meia-noite ("18:00"-"02:00") e vale ate a madrugada do dia seguinte.

export type FuncionamentoTurno = {
  inicio: string;
  fim: string;
};

export type FuncionamentoDia = {
  // 0 = domingo, como o `Date.getDay()`.
  dia: number;
  aberto: boolean;
  turnos: FuncionamentoTurno[];
};

export type FuncionamentoSituacao = {
  aberto: boolean;
  texto: string;
};

export const FUNCIONAMENTO_DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const MINUTOS_DIA = 24 * 60;
const MINUTOS_SEMANA = 7 * MINUTOS_DIA;

export function funcionamentoPadrao(): FuncionamentoDia[] {
  return FUNCIONAMENTO_DIAS.map((_, dia) => ({
    dia,
    aberto: true,
    turnos: [{ inicio: '11:00', fim: '23:00' }],
  }));
}

function minutos(hora: string): number | null {
  const partes = /^(\d{1,2}):(\d{2})$/.exec(hora || '');
  if (!partes) {
    return null;
  }
  const h = Number(partes[1]);
  const m = Number(partes[2]);
  return h < 24 && m < 60 ? h * 60 + m : null;
}

// "23h" ou "23h30": e o formato que aparece no cabecalho da loja.
function horaTexto(totalMinutos: number): string {
  const doDia = ((totalMinutos % MINUTOS_DIA) + MINUTOS_DIA) % MINUTOS_DIA;
  const h = Math.floor(doDia / 60);
  const m = doDia % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

// Cada turno vira um intervalo em minutos da semana. A copia deslocada uma semana para
// tras cobre o turno de sabado que atravessa a meia-noite e entra no domingo.
function intervalos(dias: FuncionamentoDia[]): Array<[number, number]> {
  const lista: Array<[number, number]> = [];
  dias.filter((dia) => dia.aberto).forEach((dia) => {
    (dia.turnos || []).forEach((turno) => {
      const inicio = minutos(turno.inicio);
      const fim = minutos(turno.fim);
      if (inicio === null || fim === null || inicio === fim) {
        return;
      }
      const comeco = dia.dia * MINUTOS_DIA + inicio;
      const termino = dia.dia * MINUTOS_DIA + (fim < inicio ? fim + MINUTOS_DIA : fim);
      lista.push([comeco, termino], [comeco - MINUTOS_SEMANA, termino - MINUTOS_SEMANA]);
    });
  });
  return lista;
}

// Sem horario cadastrado devolve null: a loja segue sempre aberta e sem selo, como era antes
// de existir o cadastro.
export function funcionamentoSituacao(dias: FuncionamentoDia[], agora: Date): FuncionamentoSituacao | null {
  if (!dias?.length) {
    return null;
  }
  const lista = intervalos(dias);
  const instante = agora.getDay() * MINUTOS_DIA + agora.getHours() * 60 + agora.getMinutes();

  const atual = lista.find(([comeco, termino]) => comeco <= instante && instante < termino);
  if (atual) {
    return { aberto: true, texto: `Aberto até ${horaTexto(atual[1])}` };
  }

  const proximos = lista
    .reduce((todos, [comeco]) => todos.concat([comeco, comeco + MINUTOS_SEMANA]), [] as number[])
    .filter((comeco) => comeco > instante)
    .sort((a, b) => a - b);
  if (!proximos.length) {
    return { aberto: false, texto: 'Fechado' };
  }
  const proximo = proximos[0];
  const diasAte = Math.floor(proximo / MINUTOS_DIA) - Math.floor(instante / MINUTOS_DIA);
  const quando = diasAte === 0
    ? 'hoje'
    : diasAte === 1
      ? 'amanhã'
      : FUNCIONAMENTO_DIAS[Math.floor(proximo / MINUTOS_DIA) % 7].toLowerCase();
  return { aberto: false, texto: `Fechado · abre ${quando} às ${horaTexto(proximo)}` };
}

// Uma mensagem por problema, ja com o dia, para a tela mostrar todas de uma vez.
export function funcionamentoErros(dias: FuncionamentoDia[]): string[] {
  const erros: string[] = [];
  (dias || []).filter((dia) => dia.aberto).forEach((dia) => {
    const nome = FUNCIONAMENTO_DIAS[dia.dia];
    if (!dia.turnos?.length) {
      erros.push(`${nome}: inclua um turno ou marque o dia como fechado.`);
      return;
    }
    dia.turnos.forEach((turno, i) => {
      const inicio = minutos(turno.inicio);
      const fim = minutos(turno.fim);
      const rotulo = dia.turnos.length > 1 ? `${nome}, turno ${i + 1}` : nome;
      if (inicio === null || fim === null) {
        erros.push(`${rotulo}: informe o horário de abertura e de fechamento.`);
      } else if (inicio === fim) {
        erros.push(`${rotulo}: abertura e fechamento no mesmo horário.`);
      }
    });
  });
  return erros;
}

export type FuncionamentoLinha = {
  nome: string;
  texto: string;
  hoje: boolean;
};

// Uma linha por dia, comecando na segunda, para a aba "Horario" da loja.
export function funcionamentoLinhas(dias: FuncionamentoDia[], agora: Date): FuncionamentoLinha[] {
  const ordem = [1, 2, 3, 4, 5, 6, 0];
  return ordem
    .map((numero) => (dias || []).find((dia) => dia.dia === numero))
    .filter(Boolean)
    .map((dia) => {
      const turnos = dia.aberto
        ? (dia.turnos || [])
          .filter((turno) => minutos(turno.inicio) !== null && minutos(turno.fim) !== null)
          .map((turno) => `${horaTexto(minutos(turno.inicio))} às ${horaTexto(minutos(turno.fim))}`)
        : [];
      return {
        nome: FUNCIONAMENTO_DIAS[dia.dia],
        texto: turnos.length ? turnos.join(' · ') : 'Fechado',
        hoje: dia.dia === agora.getDay(),
      };
    });
}
